import React, { useCallback, useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { Button } from "semantic-ui-react";
import { FiEdit } from "react-icons/fi";
import Swal from "sweetalert2";
import {
  fetchAccountingSettings,
  updateAccountingSettings,
} from "./accountingSettingsApi";
import { formatDecimalWithComma, parseDecimalInput, sanitizeDecimalInput } from "./decimalFormat";

export default function AccountingCurrencyRatePanel({ onRateChange, style }) {
  const [rate, setRate] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const settings = await fetchAccountingSettings();
      const value = settings?.usd_eur_rate ?? null;
      setRate(value);
      setCanEdit(settings?.can_edit === true);
      if (onRateChange) onRateChange(value);
    } finally {
      setLoaded(true);
    }
  }, [onRateChange]);

  useEffect(() => {
    load();
  }, [load]);

  const displayRate = () => {
    if (rate == null || rate === "") return "Not set";
    return `1 USD = ${formatDecimalWithComma(rate)} EUR`;
  };

  const openEdit = () => {
    setDraft(rate != null && rate !== "" ? String(rate).replace(".", ",") : "");
    setShowEdit(true);
  };

  const isValidDraft = () => {
    const trimmed = String(draft).trim();
    if (trimmed === "") return true;
    const parsed = parseDecimalInput(draft);
    return !Number.isNaN(parsed) && parsed > 0;
  };

  const handleSave = async () => {
    const trimmed = String(draft).trim();
    const parsed = trimmed === "" ? null : parseDecimalInput(draft);
    if (trimmed !== "" && (Number.isNaN(parsed) || parsed <= 0)) {
      Swal.fire("Error", "Currency rate must be a valid number greater than 0", "error");
      return;
    }

    setSaving(true);
    try {
      const settings = await updateAccountingSettings(parsed);
      const value = settings?.usd_eur_rate ?? null;
      setRate(value);
      if (onRateChange) onRateChange(value);
      setShowEdit(false);
      Swal.fire("Success", "Currency rate updated", "success");
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || "Failed to update currency rate";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #e0e0e0",
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          ...style,
        }}
      >
        <span style={{ fontSize: 13, color: "#666", fontWeight: 600 }}>Currency rate</span>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{displayRate()}</span>
        {canEdit ? (
          <button
            type="button"
            onClick={openEdit}
            title="Edit global USD/EUR currency rate"
            aria-label="Edit currency rate"
            style={{
              marginLeft: 2,
              padding: "5px 8px",
              border: "1px solid #93ab3c",
              borderRadius: 6,
              background: "#f4f7e8",
              color: "#5a6d24",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: 1,
            }}
          >
            <FiEdit size={16} />
          </button>
        ) : null}
      </div>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit currency rate (USD → EUR)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>USD → EUR rate</Form.Label>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(sanitizeDecimalInput(e.target.value))}
              placeholder="e.g. 0,92"
            />
            <Form.Text className="text-muted">
              One global rate for all users. Used when converting USD accounting entries to EUR totals.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={() => setShowEdit(false)} disabled={saving}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={saving || !isValidDraft()}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/** Top-left placement for accounting pages (20px inset). */
export function AccountingCurrencyRateBar({ barStyle, ...panelProps }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        margin: "20px 0 12px 20px",
        ...barStyle,
      }}
    >
      <AccountingCurrencyRatePanel {...panelProps} />
    </div>
  );
}
